import React from 'react';
import { Check, X, Headphones, AlertCircle, LifeBuoy, Clock, Users, ShieldCheck, Mail, MessageSquare, Phone, Activity, Star } from 'lucide-react';

const Support: React.FC = () => {
  const serviceAreas = [
    {
      icon: <Users size={32} />,
      title: "Mesa de Ayuda Nivel 1",
      description: "Atención directa a dudas funcionales de tus estudiantes y docentes. ¿Cómo subo mi tarea? ¿Cómo recupero mi contraseña?",
      benefit: "Libera a tu equipo interno de consultas repetitivas básicas."
    },
    {
      icon: <LifeBuoy size={32} />,
      title: "Soporte Técnico Nivel 2",
      description: "Resolución de problemas de configuración de cursos, calificaciones, matriculación y errores de operación en la plataforma.",
      benefit: "Solución experta a problemas que detienen la operación académica."
    },
    {
      icon: <Activity size={32} />,
      title: "Ingeniería Nivel 3",
      description: "Diagnóstico y resolución de incidencias de servidor, base de datos, rendimiento y conflictos de código/plugins.",
      benefit: "Respaldo técnico profundo para situaciones críticas."
    },
    {
      icon: <AlertCircle size={32} />,
      title: "Gestión de Incidentes Críticos",
      description: "Protocolo de emergencia ante caídas de servicio o ataques, con tiempos de respuesta (SLA) prioritarios.",
      benefit: "Restauración rápida del servicio minimizando impacto."
    },
    {
      icon: <ShieldCheck size={32} />,
      title: "Soporte Preventivo",
      description: "Revisiones periódicas de salud del sistema para detectar y corregir problemas antes de que afecten a los usuarios.",
      benefit: "Estabilidad a largo plazo y menos emergencias."
    },
    {
      icon: <Mail size={32} />,
      title: "Canales Dedicados",
      description: "Atención vía correo, portal de tickets, chat o teléfono según la criticidad y el plan contratado.",
      benefit: "Comunicación fluida y trazabilidad total de cada caso."
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "$200.000",
      period: "/mes",
      description: "Soporte Nivel 2 y 3 Reactivo",
      popular: false,
      features: [
        { text: "Hasta 10 tickets mensuales", included: true },
        { text: "Horario hábil (L-V 9-18h)", included: true },
        { text: "Tiempo respuesta < 4h", included: true },
        { text: "Portal de tickets", included: true },
        { text: "Soporte a administradores", included: true },
        { text: "Soporte a usuarios finales", included: false },
        { text: "Monitoreo proactivo", included: false },
        { text: "Informes mensuales", included: false }
      ],
      buttonText: "Contratar Básico",
      buttonClass: "btn-secondary"
    },
    {
      name: "Pro",
      price: "$450.000",
      period: "/mes",
      description: "Soporte Integral + Monitoreo",
      popular: true,
      features: [
        { text: "Hasta 30 tickets mensuales", included: true },
        { text: "Horario extendido + Sábados", included: true },
        { text: "Tiempo respuesta < 2h", included: true },
        { text: "Portal + Chat directo", included: true },
        { text: "Soporte a administradores", included: true },
        { text: "Monitoreo de servidor 24/7", included: true },
        { text: "Soporte a usuarios finales", included: false },
        { text: "Informes de servicio", included: true }
      ],
      buttonText: "Contratar Pro",
      buttonClass: "btn-primary"
    },
    {
      name: "Full 360",
      price: "A cotizar",
      period: "/mes",
      description: "Mesa de Ayuda Completa",
      popular: false,
      features: [
        { text: "Tickets ilimitados", included: true },
        { text: "Atención 24/7 emergencias", included: true },
        { text: "Tiempo respuesta < 1h", included: true },
        { text: "Multicanal (Tel/Chat/Mail)", included: true },
        { text: "Soporte a administradores", included: true },
        { text: "Mesa de ayuda a alumnos/profes", included: true },
        { text: "Gerente de cuenta dedicado", included: true },
        { text: "Capacitaciones de refuerzo", included: true }
      ],
      buttonText: "Cotizar Full",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Clock size={24} />,
      title: "Respuesta Rápida",
      description: "SLAs definidos por contrato para garantizar atención oportuna"
    },
    {
      icon: <Headphones size={24} />,
      title: "Expertos Certificados",
      description: "Personal técnico especializado en LMS Moodle y Servidores"
    },
    {
      icon: <Activity size={24} />,
      title: "Trazabilidad",
      description: "Sistema de tickets para seguimiento detallado de cada caso"
    },
    {
      icon: <MessageSquare size={24} />,
      title: "Cercanía",
      description: "Trato humano y comprensión de las urgencias académicas"
    }
  ];

  const useCases = [
    {
      title: "Inicio de Semestre",
      description: "Institución refuerza su equipo durante las semanas críticas de matrícula e inicio de clases para atender peak de consultas.",
      results: "Inicio de clases sin colapsos"
    },
    {
      title: "Externalización Total",
      description: "Universidad delega completamente la mesa de ayuda técnica en nosotros, reduciendo costos de personal interno.",
      results: "Reducción de costos operativos un 30%"
    },
    {
      title: "Respaldo Técnico",
      description: "Equipo TI interno de un colegio se apoya en nosotros solo para problemas complejos de servidor que escapan a su conocimiento.",
      results: "Resolución de fallos complejos asegurada"
    }
  ];

  return (
    <section id="support" className="support-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Headphones size={16} />
                Soporte 24/7
              </div>
              <h1 className="hero-title">
                Soporte Especializado
                <span className="highlight">Moodle & Infraestructura</span>
              </h1>
              <p className="hero-description">
                Un equipo técnico a tu disposición para resolver dudas, incidencias y urgencias. 
                Garantiza la continuidad operativa de tu campus virtual.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Contratar Soporte
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">&lt; 1h</div>
                  <div className="stat-label">Tiempo respuesta</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">98%</div>
                  <div className="stat-label">Tickets resueltos</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">NPS</div>
                  <div className="stat-label">+70 Satisfacción</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Niveles de Atención</h2>
            <p className="section-subtitle">
              Cobertura completa desde la duda más simple hasta el fallo más crítico
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>¿Por qué delegar el soporte?</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Modalidades de Servicio</h2>
            <p className="section-subtitle">
              Adaptamos nuestra operación a la realidad de tu institución
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Planes de Soporte</h2>
            <p className="section-subtitle">
              Tranquilidad mensual a un costo predecible
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Necesitas ayuda ahora mismo?</h3>
            <p>
              Si tienes una emergencia o quieres asegurar la estabilidad de tu plataforma, 
              contáctanos hoy mismo.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Solicitar Soporte
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Support;
